import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '@/lib/auth';

export default function Login() {
  const { signIn, user, authorized, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (authorized) {
      // Use direct location change as a reliable fallback; navigate hook may
      // not work reliably in this router setup.
      window.location.href = window.location.origin + window.location.pathname.replace(/\/login\/?$/, '/');
    }
  }, [authorized]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('E-posta ve şifre alanlarını doldurun.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await signIn(email.trim(), password);
      if (result.error) setError(result.error);
    } catch (e) {
      console.error('Login submit error:', e);
      setError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500 text-white">
            <i className="ri-sparkling-2-line text-2xl" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground-950">VİAYPİİ</h1>
          <p className="mt-1 text-sm text-foreground-500">
            Operasyon &amp; Finans Merkezi
          </p>
        </div>

        <div className="rounded-2xl border border-background-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-foreground-900">Panele Giriş</h2>
          <p className="mt-1 text-sm text-foreground-500">
            Yetkili hesabınızla devam edin.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                E-posta
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@sirket.com"
                className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground-700">
                Şifre
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 pr-11 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600"
                  aria-label="Şifreyi göster/gizle"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <i className="ri-error-warning-line mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {user && !authorized && !error && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-700">
                Giriş yapıldı ancak bu hesap panele yetkili değil.
              </div>
            )}

            {authLoading && !error && !submitting && (
              <div className="flex items-center justify-center gap-2 py-2 text-sm text-foreground-500">
                <i className="ri-loader-4-line animate-spin" />
                Yetki kontrol ediliyor...
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || authLoading}
              className="flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Giriş yapılıyor...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  Giriş Yap
                </>
              )}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-foreground-400">
          Yalnızca yetkili VİAYPİİ panel kullanıcıları erişebilir.
        </p>
      </div>
    </div>
  );
}